import {translations} from '../../../../../../i18n/translations';
import {Button, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {ShiftCard} from './shift-card';
import AddIcon from '@mui/icons-material/Add';
import {SystemEntitiesCardSkeleton} from '../../../../../../components/system-entities-index-list-card/skeleton';
import {ShiftsIndexViewScreen} from '../../../../../../components/shifts-index-view-screen/shifts-index-view-screen';
import {useState} from 'react';
import ActionsShiftForm from './actions-shift-flow/actions-shift-form';
import {UserType} from '../../../../../../backend/enums/user-type';
import {useParams} from 'react-router-dom';
import {EntityShiftsRepository} from '../../../../../../repositories/entity-shifts';
import {useFetchAllItems} from '../../../../../../hooks/fetch-all-items';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {useAuth} from '../../../../../../contexts/auth/context';
import {useEntity} from '../../../../../../contexts/entity/context';
import {useFetchEntityUsers} from '../../../../../../hooks/use-fetch-entity-users';

export default function ShiftsProfessionalCaregiverProfessionalCaregiverOfficeScreen() {
  const {t} = useTranslation();
  const {entityId}: {entityId: string} = useParams();
  const [createShiftModalOpened, setCreateShiftModalOpened] = useState<boolean>(false);
  const {hasWritePermission} = useAuth();
  const {state: entityState} = useEntity();
  const canWriteShifts = hasWritePermission(RolePermissionType.Team_Shifts_Management);

  // Open modal to create shift
  const openCreateShiftModal = (opened: boolean) => {
    setCreateShiftModalOpened(opened);
  };

  const {items: shifts, refreshItems: reloadShifts} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      return EntityShiftsRepository.instance.getShifts(entityId, limit, skip);
    },
  });

  const {items: collaborators} = useFetchEntityUsers({
    userType: UserType.Caregiver,
    activeUsers: true,
  });

  const closeCreateShiftModal = () => {
    reloadShifts();
    setCreateShiftModalOpened(false);
  };

  return (
    <>
      <ShiftsIndexViewScreen
        shifts={shifts}
        canWriteShifts={canWriteShifts}
        collaborators={collaborators}
        entitySocialResponses={entityState.userEntitySocialResponses}
        updateCallback={() => reloadShifts()}
        searchOptions={{
          searchString: true,
        }}
        emptyStateMessage={{
          title: t(translations.shifts_page.empty_state.title),
          subtitle: t(translations.shifts_page.empty_state.subtitle),
        }}
        rightColumn={({resultsLength}) => (
          <Stack gap={2} alignSelf="flex-end" alignItems="flex-end">
            {resultsLength ? (
              <Typography
                fontSize="14px"
                fontWeight={600}
                variant="caption"
                color="white"
                alignSelf="flex-end"
                sx={{maxWidth: '100%'}}
              >
                {t(translations.index_grid_screen.total_counter)}: {resultsLength}{' '}
                {t(
                  resultsLength === 1
                    ? translations.shifts_page.teams.singular
                    : translations.shifts_page.teams.plural
                )}
              </Typography>
            ) : null}
            {canWriteShifts && (
              <Button
                size="large"
                variant="inverted"
                startIcon={<AddIcon />}
                onClick={() => openCreateShiftModal(true)}
                sx={{
                  minWidth: 'unset',
                  width: '107px',
                  height: '40px',
                  '.MuiButton-startIcon': {
                    margin: {xs: 'inherit', md: 0},
                  },
                }}
              >
                {t(translations.shifts_page.create_button)}
              </Button>
            )}
          </Stack>
        )}
        renderItem={{
          grid: {
            element: ShiftCard,
            skeleton: SystemEntitiesCardSkeleton,
            gridItemWidth: 200,
          },
        }}
      />
      <ActionsShiftForm
        title={t(translations.shifts_page.shift_modal.create_shift)}
        shiftModalOpened={createShiftModalOpened}
        closeShiftModal={closeCreateShiftModal}
        collaborators={collaborators}
        entitySocialResponses={entityState.userEntitySocialResponses}
      />
    </>
  );
}
